import React, { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'

import { Box, Card, CardContent, Grid, Typography } from '@mui/material'
import PageHeader from '@core/components/pageHeader'
import EndPoints from 'network/Endpoints'
import APICall from 'network/APICall'
import moment from 'moment'
import { getHijriDate } from './getHijriDate'
import Loader from '@core/helper/Loader'

const initialState = {
  todayHijriDate: '',
  tomorrowHijriDate: '',
  specialDayTitle: '',
  currentMonthday: '',
  totalBusiness: '',
  totalUsers: '',
  totalUnreadFeedback: '',
  programData: null
}

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(initialState)

  const setData = useCallback(
    stateObj => {
      setDashboardData(prevState => {
        return { ...prevState, ...stateObj }
      })
    },
    [setDashboardData]
  )

  const columnStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  }

  const {
    todayHijriDate,
    tomorrowHijriDate,
    specialDayTitle,
    currentMonthday,
    totalBusiness,
    totalUsers,
    totalUnreadFeedback,
    programData
  } = dashboardData

  const getNextProgram = useCallback(
    data => {
      const payload = {
        pageNo: 1,
        limit: 100,
        orderBy: 'ASC',
        searchText: '',
        date: moment().format('yyyy-MM-DD'),
        isFutureProgram: true
      }
      APICall('post', payload, EndPoints.getProgramList)
        .then(res => {
          const { isError, message, results } = res.data.data
          if (res.status === 200 && !isError) {
            if (results.length > 0) {
              const hijriDate = getHijriDate(
                moment(results[0]?.date).utc().format('DD-MM-YYYY'),
                data
              )

              const list = {
                ...results[0],
                fullDate: hijriDate?.fullDate
              }

              setData({ programData: list })
            } else {
              setData({ programData: null })
            }
          } else {
            toast.error(message)
          }
        })
        .catch(e => {
          toast.error(e)
        })
    },
    [setData]
  )

  const getCurrentDaysCount = useCallback(
    date => {
      const payload = {
        date: `${date?.hijriMonth}-${date?.hijriYear}`
      }
      Loader.isLoading(true)
      APICall('post', payload, EndPoints.getDashboardData)
        .then(res => {
          const { isError, message, data } = res.data
          if (res.status === 200 && !isError) {
            setData({
              currentMonthday: data?.current_hijri_month_day || '',
              totalBusiness: data?.total_business || '',
              totalUsers: data?.total_user || '',
              totalUnreadFeedback: data?.total_unread_feedback || ''
            })
          } else {
            toast.error(message)
          }
        })
        .catch(e => {
          toast.error(e)
        })
        .finally(() => {
          Loader.isLoading(false)
        })
    },
    [setData]
  )

  const getAllSpecialDays = useCallback(
    tomorrowDate => {
      Loader.isLoading(true)
      APICall('get', null, EndPoints.getAllSpecialDays)
        .then(res => {
          const { isError, message, data } = res.data
          if (res.status === 200 && !isError) {
            const allSpecialDays = data
            const special = allSpecialDays.find(
              obj =>
                (obj?.is_exact_english_date &&
                  obj?.date === moment().format('MM/DD/YYYY')) ||
                (obj?.is_exact_hijri_date && obj?.full_date === tomorrowDate) ||
                (!obj?.is_exact_english_date &&
                  !obj?.is_exact_hijri_date &&
                  obj.full_date.includes(tomorrowDate?.split(',')[0]))
            )
            if (special?.special_day_title) {
              setData({ specialDayTitle: special?.special_day_title })
            }
          } else {
            toast.error(message)
          }
        })
        .catch(e => {
          toast.error(e)
        })
        .finally(() => {
          Loader.isLoading(false)
        })
    },
    [setData]
  )

  const getTodayHijriDate = useCallback(() => {
    Loader.isLoading(true)
    APICall('get', null, EndPoints.getHijriCalender)
      .then(res => {
        const { isError, message, data } = res.data
        if (res.status === 200 && !isError) {
          const hijriMonthAdjustments = data
          const todayHijri = getHijriDate(
            moment().format('DD-MM-YYYY'),
            hijriMonthAdjustments
          )
          getCurrentDaysCount(todayHijri)
          setData({ todayHijriDate: todayHijri.fullDate ?? '' })
          const tomorrowHijri = getHijriDate(
            moment().add('days', 1).format('DD-MM-YYYY'),
            hijriMonthAdjustments
          )
          getNextProgram(hijriMonthAdjustments)
          getAllSpecialDays(tomorrowHijri.fullDate)
          setData({ tomorrowHijriDate: tomorrowHijri.fullDate ?? '' })
        } else {
          toast.error(message)
        }
      })
      .catch(e => {
        toast.error(e)
      })
      .finally(() => {
        Loader.isLoading(false)
      })
  }, [getAllSpecialDays, getCurrentDaysCount, getNextProgram, setData])

  const createCards = (title, value) => {
    return (
      <CardContent sx={{ py: theme => `${theme.spacing(4)} !important` }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={columnStyles}>
            <Typography variant='h6'>{title}</Typography>
            <Typography variant='caption' sx={{ fontSize: '1rem' }}>
              {value}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    )
  }

  useEffect(() => {
    getTodayHijriDate()
  }, [getTodayHijriDate])

  return (
    <Grid container spacing={4}>
      <PageHeader
        title={
          <Typography variant='body1' sx={{ fontSize: '1.3rem !important' }}>
            Dashboard
          </Typography>
        }
      />

      <Grid container spacing={12} mt={2} ml={'-0.5rem'} mr={'1rem'}>
        <Grid item xs={12} md={4} sm={6}>
          <Card>{createCards("Today's Hijri date", todayHijriDate)}</Card>
        </Grid>

        <Grid item xs={12} md={4} sm={6}>
          <Card>
            <CardContent sx={{ py: theme => `${theme.spacing(4)} !important` }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={columnStyles}>
                  <Typography variant='h6'>
                    {"Tomorrow's Hijri date"}
                    {specialDayTitle && ' & special day'}
                  </Typography>
                  <Typography variant='caption' sx={{ fontSize: '1rem' }}>
                    {tomorrowHijriDate}
                  </Typography>
                  {specialDayTitle && (
                    <Typography variant='caption' sx={{ fontSize: '1rem' }}>
                      {specialDayTitle}
                    </Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4} sm={6}>
          <Card>
            {createCards("Current Hijri month's days", currentMonthday)}
          </Card>
        </Grid>

        <Grid item xs={12} md={4} sm={6}>
          <Card>{createCards('Registered businesses', totalBusiness)}</Card>
        </Grid>

        <Grid item xs={12} md={4} sm={6}>
          <Card>{createCards('Registered users', totalUsers)}</Card>
        </Grid>

        <Grid item xs={12} md={4} sm={6}>
          <Card>
            {createCards('Unread feedback messages', totalUnreadFeedback)}
          </Card>
        </Grid>

        <Grid item xs={12} md={4} sm={6}>
          <Card>
            <CardContent sx={{ py: theme => `${theme.spacing(4)} !important` }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={columnStyles}>
                  <Typography variant='h6'>{'Next program'}</Typography>
                  {programData === null ? (
                    <Typography variant='caption' sx={{ fontSize: '1rem' }}>
                      -
                    </Typography>
                  ) : (
                    <div style={columnStyles}>
                      <Typography variant='caption' sx={{ fontSize: '1rem' }}>
                        {programData?.title}
                      </Typography>
                      <Typography variant='caption' sx={{ fontSize: '1rem' }}>
                        {moment(programData?.date).utc().format('DD/MM/YYYY')}
                      </Typography>
                      <Typography variant='caption' sx={{ fontSize: '1rem' }}>
                        {programData?.fullDate}
                      </Typography>
                    </div>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Grid>
  )
}

export default Dashboard
